/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { NgModule } from '@angular/core';
import { Action, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TenantManagementComponent } from './components/tenant-management.component';
import { TenantListComponent } from './components/tenant-list/tenant-list.component';
import { TenantViewComponent } from './components/tenant-view/tenant-view.component';
import { TenantUsersComponent } from './components/tenant-view/tenant-users/tenant-users.component';
import { TenantCreationWizardComponent } from './components/tenant-list/tenant-creation-wizard/tenant-creation-wizard.component';
import { TenantSetupComponent } from './components/tenant-list/tenant-creation-wizard/tenant-setup/tenant-setup.component';
import { UserDetailsFormComponent } from './components/tenant-view/tenant-users/user-details-form/user-details-form.component';

import { environment } from 'environments/environment';

import { RestClientService } from 'app/shared/services/rest-client.service';

import { flattenReducers } from 'app/shared/store/utils';
import { INITIAL_STATE, TenantManagementState } from './store/tenant-management.state';
import { reducer } from './store/tenant-management.reducer';
import { reducer as apiReducer } from './store/api/tenant-management-api.reducer';

import { TenantManagementEffects } from './store/tenant-management.effects';
import { TenantManagementApiEffects } from './store/api/tenant-management-api.effects';
import { SharedModule } from 'app/shared/shared.module';
import { StaticDataModule } from 'app/static-data/module';
import { AuthGuard } from 'app/routing/auth.guard';
import { TenantDataService } from './services/data.service';
import { MockTenantDataService } from './services/mock-data.service';
import { ApiTenantDataService } from './services/api-data.service';
import { TenantManagementCommonModule } from './modules/common/module';
import { TenantLoginComponent } from './components/tenant-login/tenant-login.component';
import { MockSharedDataService } from 'app/shared-data/services/mock-shared-data.service';
import { HttpClient } from '@angular/common/http';
import { TenantUsersFilterComponent } from 'app/page-modules/tenant-management/components/tenant-view/tenant-users/tenant-users-filter/tenant-users-filter.component';
import { MatFormFieldModule } from '@angular/material/form-field';

// Formats used in the date picker
export const DATE_FORMATS = {
  parse: {
    dateInput: 'yyyy-LL-dd'
  },
  display: {
    dateInput: 'yyyy-LL-dd',
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'LLL yyyy'
  }
};

@NgModule({
  declarations: [
    TenantManagementComponent,
    TenantLoginComponent,
    TenantListComponent,
    TenantViewComponent,
    TenantUsersComponent,
    TenantUsersFilterComponent,
    TenantCreationWizardComponent,
    TenantSetupComponent,
    UserDetailsFormComponent
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    MatTabsModule,
    MatExpansionModule,
    MatSidenavModule,
    MatStepperModule,
    MatDatepickerModule,
    MatRadioModule,
    MatCheckboxModule,
    SharedModule,
    StaticDataModule,
    TenantManagementCommonModule,
    StoreModule.forFeature('page-tenant-management', tenantManagementReducer),
    EffectsModule.forFeature([TenantManagementApiEffects, TenantManagementEffects]),
    MatButtonModule,
    RouterModule.forChild([
      {
        path: '',
        component: TenantManagementComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: TenantListComponent,
            canActivate: [AuthGuard]
          },
          {
            path: ':tenantId',
            component: TenantViewComponent,
            children: [
              {
                path: '',
                redirectTo: 'activity',
                pathMatch: 'full'
              },
              {
                path: 'activity',
                loadChildren: () =>
                  import('app/page-modules/tenant-management/modules/activity/module').then(
                    m => m.ActivityModule
                  )
              },
              {
                path: 'calculations',
                loadChildren: () =>
                  import(
                    'app/page-modules/tenant-management/modules/calculations/calculations.module'
                  ).then(m => m.CalculationsModule)
              },
              {
                path: 'users',
                component: TenantUsersComponent
              },
              {
                path: 'details',
                loadChildren: () =>
                  import('app/page-modules/tenant-management/modules/details/module').then(
                    m => m.DetailsModule
                  )
              },
              {
                path: 'client-files',
                loadChildren: () =>
                  import('app/page-modules/tenant-management/modules/client-files/module').then(
                    m => m.ClientFilesModule
                  )
              },
              {
                path: 'login',
                pathMatch: 'full',
                component: TenantLoginComponent
              },
              {
                path: 'login/:preview',
                pathMatch: 'full',
                component: TenantLoginComponent
              },
              {
                path: 'valuation-dates',
                loadChildren: () =>
                  import('app/page-modules/tenant-management/modules/valuation-dates/module').then(
                    m => m.ValuationDatesModule
                  )
              }
            ]
          }
        ]
      }
    ])
  ],
  providers: [
    {
      provide: TenantDataService,
      useFactory: tenantsServiceFactory,
      deps: [RestClientService, HttpClient]
    }
  ]
})
export class TenantManagementModule {}

/** Flattens the tenant management reducers */
export function tenantManagementReducer(
  state: TenantManagementState,
  action: Action
): TenantManagementState {
  return flattenReducers(INITIAL_STATE, reducer, apiReducer)(state, action);
}

// -------------------------------------------------------------------------
/** Provides the tenants service. */
export function tenantsServiceFactory(restClientService: RestClientService): TenantDataService {
  if (environment.dataProvider === 'mock') {
    const sharedDataService = new MockSharedDataService();
    return new MockTenantDataService(sharedDataService);
  }
  return new ApiTenantDataService(restClientService);
}
